import { useEffect, useRef, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { SlidersHorizontal } from "lucide-react";
import type {
  PaidMediaOverviewFiltersData,
  PaidMediaOverviewGranularity,
  PaidMediaOverviewPlatformFilter,
} from "src/services/PaidMediaOverviewAPI";
import OrganicDateRangePicker from "src/dashboards/organicMedia/components/Tabs/OrganicDateRangePicker";
import { SelectMenu } from "src/dashboards/salesProductAnalytics/components/salesProductShared";
import { DefinitionTooltip } from "src/components/common";
import type { OverviewAttributionSource } from "../../utils/paidMediaOverviewAttributionContext";
import {
  overviewPlatformLabel,
  overviewStatusLabel,
} from "../../utils/paidMediaOverviewLabels";
import { OverviewSegmented } from "./overviewPrimitives";

export interface PaidMediaOverviewHeaderState {
  attribution: OverviewAttributionSource;
  granularity: PaidMediaOverviewGranularity;
  startDate: string;
  endDate: string;
  platform: PaidMediaOverviewPlatformFilter;
  campaignId: string;
  objectiveOrNetwork: string;
  status: string;
}

interface FilterOption {
  value: string;
  label: string;
}

interface FilterRow {
  id: string;
  label: string;
  value: string;
  options: FilterOption[];
  onChange: (_value: string) => void;
}

interface PaidMediaOverviewHeaderProps {
  state: PaidMediaOverviewHeaderState;
  filters: PaidMediaOverviewFiltersData | null;
  onChange: (_patch: Partial<PaidMediaOverviewHeaderState>) => void;
  storeRevenueAvailable: boolean;
}

const ALL_OPTION_VALUE = "";

const FILTER_DROPDOWN_CLASS = "paid-media-overview__filter-select-dropdown";

const CAMPAIGN_LEVEL_FILTER_IDS = new Set(["campaign", "objectiveOrNetwork", "classification"]);

const PaidMediaOverviewHeader: FC<PaidMediaOverviewHeaderProps> = ({
  state,
  filters,
  onChange,
  storeRevenueAvailable,
}) => {
  const { t } = useTranslation();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const moreFiltersRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!filtersOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }
      if (moreFiltersRef.current?.contains(target)) {
        return;
      }
      if (target.closest(`.${FILTER_DROPDOWN_CLASS}`)) {
        return;
      }
      setFiltersOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setFiltersOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [filtersOpen]);

  const allLabel = t("paidMediaOverview.header.filterAll");
  const isStoreValidated = state.attribution === "shopify";
  const storeToggleDisabled = !storeRevenueAvailable;
  const storeToggleTooltip = !storeRevenueAvailable
    ? t("paidMediaOverview.header.attributionStoreUnavailable")
    : t("paidMediaOverview.header.attributionStoreHelp");
  const campaignFiltersDisabledHelp = t(
    "paidMediaOverview.header.campaignFiltersStoreValidatedDisabled"
  );

  const filterRows: FilterRow[] = [
    {
      id: "platform",
      label: t("paidMediaOverview.header.filters.platform"),
      value: state.platform,
      options: [
        { value: "all", label: allLabel },
        ...(filters?.platforms ?? []).map(platform => ({
          value: platform?.value,
          label: overviewPlatformLabel(t, platform?.value) || platform?.label,
        })),
      ],
      onChange: value => onChange({ platform: value as PaidMediaOverviewPlatformFilter }),
    },
    {
      id: "campaign",
      label: t("paidMediaOverview.header.filters.campaign"),
      value: state.campaignId,
      options: [
        { value: ALL_OPTION_VALUE, label: t("paidMediaOverview.header.filterAllCampaigns") },
        ...(filters?.campaigns ?? []).map(campaign => ({
          value: campaign?.campaign_id,
          label: campaign?.campaign_name,
        })),
      ],
      onChange: value => onChange({ campaignId: value }),
    },
    {
      id: "objectiveOrNetwork",
      label: t("paidMediaOverview.header.filters.objectiveOrNetwork"),
      value: state.objectiveOrNetwork,
      options: [
        { value: ALL_OPTION_VALUE, label: allLabel },
        ...(filters?.objectives_or_networks ?? []).map(option => ({
          value: option,
          label: option,
        })),
      ],
      onChange: value => onChange({ objectiveOrNetwork: value }),
    },
    {
      id: "classification",
      label: t("paidMediaOverview.header.filters.classification"),
      value: state.status,
      options: [
        { value: ALL_OPTION_VALUE, label: allLabel },
        ...(filters?.statuses ?? []).map(status => ({
          value: status?.value,
          label: overviewStatusLabel(t, status?.value, status?.label),
        })),
      ],
      onChange: value => onChange({ status: value }),
    },
  ];

  return (
    <div className="paid-media-overview__header">
      <div className="paid-media-overview__header-copy">
        <h1 className="paid-media-overview__title">{t("paidMediaOverview.title")}</h1>
        <p className="paid-media-overview__subtitle">{t("paidMediaOverview.subtitle")}</p>
      </div>

      <div className="paid-media-overview__header-actions">
        <div className="paid-media-overview__header-row">
          <OverviewSegmented
            value={state.attribution}
            onChange={value => onChange({ attribution: value })}
            ariaLabel={t("paidMediaOverview.header.attributionAria")}
            options={[
              {
                value: "platform",
                label: t("paidMediaOverview.header.attributionPlatform"),
                tooltip: { description: t("paidMediaOverview.header.attributionPlatformHelp") },
              },
              {
                value: "shopify",
                label: t("paidMediaOverview.header.attributionStore"),
                disabled: storeToggleDisabled,
                tooltip: { description: storeToggleTooltip },
              },
            ]}
          />
        </div>

        <div className="paid-media-overview__header-row paid-media-overview__header-row--bottom">
          <div className="paid-media-overview__control paid-media-overview__control--wide">
            <OrganicDateRangePicker
              startDate={state.startDate}
              endDate={state.endDate}
              onRangeChange={(range: { startDate: string; endDate: string }) =>
                onChange({ startDate: range.startDate, endDate: range.endDate })
              }
            />
          </div>
          <div
            className={
              filtersOpen
                ? "paid-media-overview__more-filters paid-media-overview__more-filters--open"
                : "paid-media-overview__more-filters"
            }
            ref={moreFiltersRef}
          >
            <button
              type="button"
              className="paid-media-overview__more-filters-trigger"
              onClick={() => setFiltersOpen(open => !open)}
              aria-expanded={filtersOpen}
            >
              <SlidersHorizontal size={14} aria-hidden />
              {t("paidMediaOverview.header.moreFilters")}
            </button>
            {filtersOpen ? (
              <div
                className="paid-media-overview__more-filters-panel"
                role="dialog"
                aria-label={t("paidMediaOverview.header.moreFiltersPanelAria")}
              >
                {filterRows.map(row => {
                  const isCampaignLevelFilter = CAMPAIGN_LEVEL_FILTER_IDS.has(row.id);
                  const disabled = isStoreValidated && isCampaignLevelFilter;
                  const displayValue = disabled ? ALL_OPTION_VALUE : row.value;

                  return (
                    <div
                      key={row.id}
                      className={
                        disabled
                          ? "paid-media-overview__filter-row paid-media-overview__filter-row--disabled"
                          : "paid-media-overview__filter-row"
                      }
                    >
                      <div className="paid-media-overview__filter-label-row">
                        <span className="paid-media-overview__filter-label">{row.label}</span>
                        {disabled ? (
                          <DefinitionTooltip
                            label={row.label}
                            info={{ description: campaignFiltersDisabledHelp }}
                          />
                        ) : null}
                      </div>
                      <SelectMenu
                        value={displayValue}
                        options={row.options}
                        onChange={row.onChange}
                        disabled={disabled}
                        className={
                          disabled
                            ? "paid-media-overview__filter-select paid-media-overview__filter-select--disabled"
                            : "paid-media-overview__filter-select"
                        }
                        dropdownClassName="paid-media-overview__filter-select-dropdown"
                        ariaLabel={row.label}
                      />
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaidMediaOverviewHeader;
